import React, { FC, useMemo } from 'react'

import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { format } from 'date-fns'
import { Form, Formik, FormikHelpers } from 'formik'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import { validationSchema } from './validationSchema'
import BlackKnight from '@/assets/img/black-knight.svg'
import Cape from '@/assets/img/cape-logo.svg'
import ClearCapital from '@/assets/img/clear-capital.png'
import CoreLogic from '@/assets/img/core-logic.svg'
import HouseCanary from '@/assets/img/house-canary.webp'
import bgImage from '@/assets/img/pull-credit-bg.svg'
import LoadingIcon from '@/components/LoadingIcon'
import { useGetApplicationQuery } from '@/graphql/services/application'
import { KeyTabNavigation } from '@/key-components/KeyTabNavigation'
import { KeyInputField } from '@/key-components/formik/KeyInputField'
import { useUpdateAddressMutation } from '@/services/address'
import { useReRunAvmsMutation } from '@/services/v2-application/valuation'
import { shallowObjectCompare } from '@/utils'

export type AvmReportModalProps = {
  onClose: () => void
}

const tabs = {
  order_details: {
    title: 'Order Details',
  },
  order_status: {
    title: 'Order Status',
  },
}

const avms = [
  {
    logo: BlackKnight,
    primaryText: 'AVM Cascade',
    secondaryText: 'AVM $9.78/pull',
    key: 'black knight',
  },
  {
    logo: CoreLogic,
    primaryText: 'Total Home Value Consumer',
    secondaryText: 'AVM $0.10/pull',
    key: 'corelogic',
  },
  /*
  Add when the backend is implemented  
  {
    logo: CoreLogic,
    primaryText: 'Total Home Value Risk Grade',
    secondaryText: 'AVM $6.70/pull',
    key: 'core_logic_risk_grade',
  }, 
  */
  {
    logo: HouseCanary,
    primaryText: 'Zip-Level HPA forecast and Value Anlysis',
    secondaryText: 'AVM $2.00/pull',
    key: 'house canary',
  },
  {
    logo: ClearCapital,
    primaryText: 'Clear Capital',
    secondaryText: 'AVM $3.50/pull',
    key: 'clear capital',
  },
  {
    logo: Cape,
    primaryText: 'Cape Analytics',
    secondaryText: '',
    key: 'cape analytics',
  },
]

const modalTitle = (
  <p>
    <b>Order 3rd Party Report | </b> <span className='pl-1'>AVM(s) Selected</span>
  </p>
)

export const AvmReportModal: FC<AvmReportModalProps> = ({ onClose }) => {
  const router = useRouter()
  const { id } = router.query
  const { data: application } = useGetApplicationQuery(
    { externalId: id as string },
    {
      skip: !id,
    }
  )

  const [reRunAvms] = useReRunAvmsMutation()
  const [updateAddress] = useUpdateAddressMutation()

  const initialValues = useMemo(() => {
    const { street, unitNumber, city, state, zipCode } = application.property.address
    return {
      action: 'order_details',
      street: street || '',
      unit_number: unitNumber || '',
      city: city || '',
      state: state || '',
      zip_code: zipCode || '',
      selected_avms: [],
      requestSend: false,
      orderedDate: null,
    }
  }, [])

  const handleSelectAVM =
    (
      currentAvms: string[],
      value: string,
      setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
      action: string
    ) =>
    () => {
      if (action === 'order_status') return
      if (currentAvms.includes(value)) {
        setFieldValue(
          'selected_avms',
          currentAvms.filter((avm) => avm !== value)
        )
        return
      }
      setFieldValue('selected_avms', [...currentAvms, value])
    }

  const handleSubmit = async (
    values,
    { resetForm, setFieldValue, validateForm }: FormikHelpers<any>
  ) => {
    if (values.action === 'order_status') {
      onClose()
      resetForm()
      return
    } else if (values.action === 'order_details') {
      try {
        setFieldValue('orderedDate', new Date())
        setFieldValue('action', 'order_status')
        setFieldValue('requestSend', true)
        const addressValues = {
          street: values.street,
          unit_number: values.unit_number,
          city: values.city,
          state: values.state,
          zip_code: values.zip_code,
        }
        const originalValues = {
          street: application.property.address.street,
          unit_number: application.property.address.unitNumber,
          city: application.property.address.city,
          state: application.property.address.state,
          zip_code: application.property.address.zipCode,
        }
        if (!shallowObjectCompare(addressValues, originalValues))
          await updateAddress({ ...addressValues, uuid: application.property.address.uuid })
        await reRunAvms({ data: { avms: values.selected_avms, applicationUUID: application.uuid } })
        setFieldValue('requestSend', false)
        setTimeout(() => {
          validateForm()
        }, 0)
      } catch (error) {
        setFieldValue('orderedDate', null)
        setFieldValue('action', 'order_details')
        setFieldValue('requestSend', false)
        toast.error(() => `There was an error on the request, please try again`)
      }
    }
  }

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnMount
      >
        {({ values, isValid, setFieldValue }) => {
          const { action, selected_avms, street, state, requestSend, orderedDate } = values
          return (
            <div
              id='overlay'
              className='top-0 left-0 fixed w-screen h-screen bg-gray-400/60 flex items-center justify-center'
              style={{ zIndex: 999 }}
            >
              <div
                className='flex flex-col gap-4 w-[80%] bg-white rounded-lg pb-2'
                style={{ maxHeight: '90vh' }}
              >
                <div className='flex justify-between border-b-2 pl-8 p-4 bg-sky-200 text-black rounded-tl-lg rounded-tr-lg'>
                  <h5>{modalTitle}</h5>
                  <FontAwesomeIcon
                    icon={faXmark}
                    width={12}
                    className='cursor-pointer'
                    onClick={() => onClose()}
                  />
                </div>

                <div className='text-sm'>
                  <Form>
                    <div className='px-8'>
                      <KeyTabNavigation
                        disabled={requestSend}
                        tabs={tabs}
                        activeTab={values.action}
                        onTabClick={(tab) => {
                          setFieldValue('action', tab)
                          if (action === 'order_status' && tab === 'order_details') {
                            setFieldValue('orderedDate', null)
                            setFieldValue('requestSend', false)
                          }
                        }}
                      />
                    </div>
                    {!action ? (
                      <div className='h-96 flex items-center justify-center flex-col bg-[#f5f5f5]'>
                        <Image
                          src={bgImage}
                          alt='AVM Report'
                        ></Image>
                        <div className='text-gray-400'>Select an action to begin</div>
                      </div>
                    ) : (
                      <>
                        {action === 'order_status' && !orderedDate && (
                          <div className='h-96 flex items-center justify-center flex-col bg-[#f5f5f5]'>
                            <Image
                              src={bgImage}
                              alt='AVM Report'
                            ></Image>
                            <div className='text-gray-400'>Currently there are no orders</div>
                          </div>
                        )}
                        {action === 'order_details' ||
                        (action === 'order_status' && orderedDate) ? (
                          <div
                            className='relative overflow-auto px-10 py-6 bg-[#f5f5f5]'
                            style={{ maxHeight: '60vh' }}
                          >
                            {requestSend && (
                              <div className='absolute left-0 top-0 px-8 flex flex-col items-center justify-center w-full h-full bg-[#0000000d]'>
                                <LoadingIcon
                                  width={80}
                                  height={80}
                                />
                              </div>
                            )}
                            <div className='h-100 flex p-4 bg-white rounded-lg'>
                              <div className='w-2/6 px-6 flex gap-1 flex-col text-[#3f3f3f] font-light'>
                                <h5 className='mb-1 text-[16px]'>Property Details</h5>
                                <div className='w-full'>
                                  <h5 className='mb-1 text-[11px]'>Address</h5>
                                  <KeyInputField
                                    disabled={action === 'order_status'}
                                    placeholder='Address'
                                    name='street'
                                    data-test='subject-property-address-input'
                                    showError
                                    className='w-full'
                                  />
                                </div>
                                <div className='w-full'>
                                  <h5 className='mb-1 text-[11px]'>Unit</h5>
                                  <KeyInputField
                                    disabled={action === 'order_status'}
                                    placeholder='Unit Number'
                                    name='unit_number'
                                    data-test='unit-number-input'
                                    showError
                                    className='w-full'
                                  />
                                </div>
                                <div className='w-full'>
                                  <h5 className='mb-1 text-[11px]'>City</h5>
                                  <KeyInputField
                                    disabled={action === 'order_status'}
                                    placeholder='City'
                                    name='city'
                                    data-test='city-input'
                                    showError
                                    className='w-full'
                                  />
                                </div>
                                <div className='w-full'>
                                  <h5 className='mb-1 text-[11px]'>State</h5>
                                  <KeyInputField
                                    disabled={action === 'order_status'}
                                    placeholder='State'
                                    name='state'
                                    data-test='state-input'
                                    showError
                                    className='w-full'
                                  />
                                </div>
                                <div className='w-full'>
                                  <h5 className='mb-1 text-[11px]'>Zip Code</h5>
                                  <KeyInputField
                                    disabled={action === 'order_status'}
                                    placeholder='Zip Code'
                                    name='zip_code'
                                    data-test='zipcode-input'
                                    showError
                                    className='w-full'
                                  />
                                </div>
                              </div>
                              <div className='ml-6 w-4/6 px-4 text-[#3f3f3f] font-light'>
                                <h5 className='mb-1 text-[16px]'>
                                  Select the AVM(s) you required *
                                </h5>
                                <div className='p-4 flex flex-row overflow-auto gap-6'>
                                  {avms.map((avm) => (
                                    <div
                                      key={avm.key}
                                      className='flex flex-col gap-3 items-center w-1/6'
                                      onClick={handleSelectAVM(
                                        selected_avms,
                                        avm.key,
                                        setFieldValue,
                                        action
                                      )}
                                    >
                                      <div
                                        className={`transition-all duration-[200ms] p-3 flex flex-col items-center justify-center w-[100px] h-[100px] rounded-xl shadow-[0_2px_7px_rgb(0,0,0,0.2)] ${
                                          selected_avms.includes(avm.key) &&
                                          'bg-[#CFDFEF] border border-[#3A7BBF]'
                                        } ${action !== 'order_status' && 'cursor-pointer'}`}
                                      >
                                        <Image
                                          src={avm.logo}
                                          alt={avm.key}
                                          style={{
                                            height: avm.key === 'cape_analytics' ? '40%' : 'auto',
                                          }}
                                        ></Image>
                                      </div>
                                      <div className='text-center text-[11px] text-[#3f3f3f] font-light flex flex-col'>
                                        <span>{avm.primaryText}</span>
                                        <span>{avm.secondaryText}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </>
                    )}
                    {action && (
                      <div className='flex justify-between items-center pt-5 py-4 px-8'>
                        {action === 'order_details' || !orderedDate ? (
                          <>
                            {!isValid ? (
                              <div className='text-[#3f3f3f] font-light'>
                                Order has not been placed. Check the Property Address and select the
                                AVM(s) you require
                              </div>
                            ) : (
                              <div className='text-[#3f3f3f] font-light'>
                                You can now place an order.
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {orderedDate && requestSend ? (
                              <>
                                <div className='text-[#3f3f3f] font-light'>
                                  The seleted AVM(s) for {street}, {state} were ordered on{' '}
                                  {format(orderedDate, 'MMM Mo yyyy @ h:mm:ss aaaa')}
                                </div>
                              </>
                            ) : (
                              <div className='text-[#3f3f3f] font-light'>AVM(s) retrieved</div>
                            )}
                          </>
                        )}
                        {(isValid || action === 'order_details') && (
                          <button
                            className='self-end rounded bg-blue-500 disabled:bg-sky-300 text-white p-2'
                            type='submit'
                            disabled={!isValid || requestSend}
                          >
                            {action === 'order_status'
                              ? 'Close Order Form'
                              : 'Finish - Order AVM(s)'}
                          </button>
                        )}
                      </div>
                    )}
                  </Form>
                </div>
              </div>
            </div>
          )
        }}
      </Formik>
    </>
  )
}
